import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Radio,
  ScanLine,
  PenLine,
  History,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  CreditCard,
  LoaderCircle,
  ExternalLink,
  Plus,
  Power,
  PowerOff,
  ChevronLeft,
  ChevronRight,
  Save,
  Keyboard,
} from 'lucide-react'

import api from '../../lib/axios'

/* =========================================================
   STYLES DE BASE
   (les variantes mobile sont calculées dans le composant)
========================================================= */

const styles = {
  panel: {
    background: '#fff',
    border: '1px solid #eee7dd',
    borderRadius: 14,
    padding: 22,
    marginTop: 20,
  },
  button: {
    border: 'none',
    borderRadius: 9,
    background: '#8b6b3f',
    color: '#fff',
    padding: '10px 15px',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  secondary: {
    border: '1px solid #e7ded1',
    borderRadius: 9,
    background: '#f4efe7',
    color: '#5f4930',
    padding: '9px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
  input: {
    boxSizing: 'border-box',
    padding: 10,
    border: '1px solid #d6d3d1',
    borderRadius: 8,
    background: '#fff',
    maxWidth: '100%',
  },
  cell: {
    padding: 13,
    borderBottom: '1px solid #f1ede7',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
}

// Actualisation automatique de la liste des cartes (temps réel par sondage)
const LIVE_REFRESH_MS = 20000

/* =========================================================
   HELPERS
========================================================= */

// Hook responsive : suit une media query et se met à jour au redimensionnement.
function useMediaQuery(query) {
  const getMatch = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(query).matches

  const [matches, setMatches] = useState(getMatch)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined

    const media = window.matchMedia(query)
    const onChange = (event) => setMatches(event.matches)

    setMatches(media.matches)

    if (media.addEventListener) {
      media.addEventListener('change', onChange)
      return () => media.removeEventListener('change', onChange)
    }

    media.addListener(onChange)
    return () => media.removeListener(onChange)
  }, [query])

  return matches
}

const normalizeList = (response) => {
  const body = response?.data

  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body)) return body

  return []
}

const statusInfo = (status) => {
  const map = {
    active: { label: 'Active', bg: '#dcfce7', color: '#166534' },
    pending: { label: 'En attente', bg: '#fef3c7', color: '#92400e' },
    disabled: { label: 'Désactivée', bg: '#fee2e2', color: '#991b1b' },
    lost: { label: 'Perdue', bg: '#f3e8ff', color: '#6b21a8' },
  }

  return map[status] || {
    label: status || 'Inconnue',
    bg: '#f3f4f6',
    color: '#374151',
  }
}

const getErrorMessage = (error) => {
  if (error.response?.status === 401) {
    return 'Session expirée. Reconnectez-vous à Tapio.'
  }

  if (error.response?.status === 403) {
    return "Accès refusé. Vérifiez votre compte administrateur."
  }

  if (error.response?.status === 404) {
    return 'Route ou ressource introuvable. Vérifiez les routes Laravel.'
  }

  if (error.response?.status === 422) {
    const errors = error.response?.data?.errors

    if (errors) {
      return Object.values(errors).flat().join(' ')
    }
  }

  return (
    error.response?.data?.message ||
    error.message ||
    'Une erreur est survenue.'
  )
}

// Compare deux UID sans tenir compte des ":" et de la casse (04:A1:... = 04a1...)
const normalizeUid = (value) =>
  String(value || '').replace(/[^0-9a-z]/gi, '').toLowerCase()

// Icône de chargement animée (l'animation est définie dans le <style> du composant)
const Spinner = ({ size = 16, style }) => (
  <LoaderCircle size={size} className="nfc-spin" style={style} />
)

const StatusBadge = ({ status }) => {
  const info = statusInfo(status)

  return (
    <span
      style={{
        display: 'inline-block',
        borderRadius: 20,
        padding: '5px 10px',
        background: info.bg,
        color: info.color,
        fontWeight: 700,
        fontSize: 13,
        whiteSpace: 'nowrap',
      }}
    >
      {info.label}
    </span>
  )
}

/* =========================================================
   COMPOSANT
========================================================= */

const AdminNfcTools = () => {
  // < 640px : téléphone  |  < 1100px : tablette / petit portable (liste de cartes)
  // >= 1100px : tableau complet
  const isMobile = useMediaQuery('(max-width: 640px)')
  const compact = useMediaQuery('(max-width: 1100px)')

  const [activeTab, setActiveTab] = useState('cards')

  const [cards, setCards] = useState([])
  const [pages, setPages] = useState([])
  const [loadingCards, setLoadingCards] = useState(false)
  const [loadingPages, setLoadingPages] = useState(false)
  const [cardsError, setCardsError] = useState('')
  const [pagesError, setPagesError] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  })

  const [quantity, setQuantity] = useState('5')
  const [creatingCards, setCreatingCards] = useState(false)
  const [busyCardId, setBusyCardId] = useState(null)

  const [supported] = useState(
    typeof window !== 'undefined' &&
      'NDEFReader' in window &&
      window.isSecureContext
  )

  const [reading, setReading] = useState(false)
  const [readError, setReadError] = useState('')
  const [records, setRecords] = useState([])
  const [serialNumber, setSerialNumber] = useState('')
  const [writing, setWriting] = useState(false)
  const [writeText, setWriteText] = useState('')

  // Écrit automatiquement l'URL de la carte sur un tag vierge juste après son ajout
  const [autoWrite, setAutoWrite] = useState(true)

  // Saisie manuelle (appareil sans Web NFC : iPhone, ordinateur…)
  const [manualValue, setManualValue] = useState('')

  // Carte détectée lors de la lecture (reconnue ou ajoutée automatiquement)
  const [scannedCard, setScannedCard] = useState(null)
  const [scanCreated, setScanCreated] = useState(false)
  const [registering, setRegistering] = useState(false)
  const [savingScanned, setSavingScanned] = useState(false)
  const [cardForm, setCardForm] = useState({
    short_code: '',
    chip_uid: '',
    page_id: '',
  })

  const [history, setHistory] = useState([])
  const [notice, setNotice] = useState(null)

  // Refs : le callback NFC et le sondage gardent une copie figée du state,
  // on lit donc les refs pour avoir toujours la valeur à jour.
  const cardsRef = useRef([])
  cardsRef.current = cards
  const paginationRef = useRef(pagination)
  paginationRef.current = pagination
  const busyRef = useRef(null)
  busyRef.current = busyCardId
  const autoWriteRef = useRef(true)
  autoWriteRef.current = autoWrite
  const abortRef = useRef(null)

  /* ---------- Styles adaptatifs ---------- */
  const panel = {
    ...styles.panel,
    padding: isMobile ? 14 : 22,
    marginTop: isMobile ? 14 : 20,
    borderRadius: isMobile ? 12 : 14,
  }

  // Sur mobile : cibles tactiles de 44px et police 16px (évite le zoom automatique d'iOS)
  const button = {
    ...styles.button,
    ...(isMobile ? { minHeight: 44 } : {}),
  }

  const secondary = {
    ...styles.secondary,
    ...(isMobile ? { minHeight: 44 } : {}),
  }

  const input = {
    ...styles.input,
    ...(isMobile ? { minHeight: 44, fontSize: 16 } : {}),
  }

  // Zone de boutons : en colonne pleine largeur sur mobile
  const fullOnMobile = isMobile ? { flex: '1 1 100%' } : {}

  const notify = (message, type = 'success') => {
    setNotice({ message, type })
  }

  const addHistory = (action, details) => {
    setHistory((previous) => [
      {
        id: `${Date.now()}-${Math.random()}`,
        action,
        details,
        date: new Date().toLocaleString(),
      },
      ...previous,
    ].slice(0, 50))
  }

  // silent = actualisation en arrière-plan : pas de spinner, pas de message d'erreur
  const loadCards = useCallback(async (pageNumber = 1, { silent = false } = {}) => {
    if (!silent) {
      setLoadingCards(true)
      setCardsError('')
    }

    try {
      const response = await api.get('/admin/cards', {
        params: { page: pageNumber },
      })

      const body = response.data
      const meta = body?.meta
      const list = normalizeList(response)

      setCards(list)

      setPagination({
        currentPage: Number(meta?.current_page || pageNumber),
        lastPage: Number(meta?.last_page || 1),
        total: Number(meta?.total ?? list.length),
        perPage: Number(meta?.per_page || 20),
      })

      setLastUpdated(new Date())
    } catch (error) {
      if (!silent) setCardsError(getErrorMessage(error))
    } finally {
      if (!silent) setLoadingCards(false)
    }
  }, [])

  const loadPages = useCallback(async () => {
    setLoadingPages(true)
    setPagesError('')

    try {
      // Hypothèse : GET /pages retourne les pages disponibles pour le compte courant.
      const response = await api.get('/pages')
      setPages(normalizeList(response))
    } catch (error) {
      setPages([])
      setPagesError(
        `Impossible de charger les pages : ${getErrorMessage(error)}`
      )
    } finally {
      setLoadingPages(false)
    }
  }, [])

  useEffect(() => {
    loadCards(1)
    loadPages()
  }, [loadCards, loadPages])

  // Temps réel : la liste se rafraîchit seule (onglet Cartes, page visible, aucune action en cours)
  useEffect(() => {
    if (activeTab !== 'cards') return undefined

    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (busyRef.current) return

      loadCards(paginationRef.current.currentPage, { silent: true })
    }, LIVE_REFRESH_MS)

    return () => clearInterval(timer)
  }, [activeTab, loadCards])

  // Les notifications disparaissent seules (utile sur mobile)
  useEffect(() => {
    if (!notice) return undefined

    const timer = setTimeout(() => setNotice(null), 6000)
    return () => clearTimeout(timer)
  }, [notice])

  // Arrête le scan NFC si on quitte la page
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const createCards = async () => {
    const amount = Number(quantity)

    if (!Number.isInteger(amount) || amount < 1 || amount > 100) {
      notify('Choisissez une quantité entre 1 et 100.', 'error')
      return
    }

    setCreatingCards(true)

    try {
      const response = await api.post('/admin/cards/generate-batch', {
        quantity: amount,
      })

      notify(response.data?.message || 'Cartes créées avec succès.')
      addHistory('Création de cartes', `${amount} carte(s) demandée(s)`)
      setActiveTab('cards')
      await loadCards(1)
    } catch (error) {
      notify(getErrorMessage(error), 'error')
    } finally {
      setCreatingCards(false)
    }
  }

  const assignPage = async (card, pageValue) => {
    const pageId = pageValue === '' ? null : Number(pageValue)

    if (!pageId) {
      notify(
        'La route actuelle exige un page_id. Pour retirer une association, utilisez la fiche de la carte (onglet Lecture NFC).',
        'error'
      )
      return
    }

    setBusyCardId(card.id)

    try {
      // PUT /cards/{card}/assign
      const response = await api.put(`/cards/${card.id}/assign`, {
        page_id: pageId,
      })

      const updatedCard = response.data?.data || response.data

      setCards((previous) =>
        previous.map((item) =>
          item.id === card.id
            ? {
                ...item,
                ...updatedCard,
                page: updatedCard?.page || pages.find(
                  (page) => Number(page.id) === pageId
                ) || item.page,
                page_id: pageId,
              }
            : item
        )
      )

      addHistory(
        'Association de page',
        `${card.short_code} → page #${pageId}`
      )

      notify('Page associée avec succès.')
    } catch (error) {
      notify(getErrorMessage(error), 'error')
      await loadCards(pagination.currentPage)
    } finally {
      setBusyCardId(null)
    }
  }

  const changeCardStatus = async (card, action) => {
    setBusyCardId(card.id)

    try {
      const response = await api.post(`/cards/${card.id}/${action}`)
      const returned = response.data?.data || response.data

      const newStatus = action === 'enable' ? 'active' : 'disabled'
      const patch = {
        ...(returned?.id ? returned : {}),
        status: returned?.status || newStatus,
        disabled_at:
          returned?.disabled_at ??
          (action === 'enable' ? null : new Date().toISOString()),
      }

      setCards((previous) =>
        previous.map((item) =>
          item.id === card.id ? { ...item, ...patch } : item
        )
      )

      // Garde la fiche « carte détectée » synchronisée
      setScannedCard((previous) =>
        previous && previous.id === card.id
          ? { ...previous, ...patch }
          : previous
      )

      addHistory(
        action === 'enable' ? 'Activation' : 'Désactivation',
        `Carte ${card.short_code}`
      )

      notify(
        response.data?.message ||
          (action === 'enable'
            ? 'Carte activée.'
            : 'Carte désactivée.')
      )
    } catch (error) {
      notify(getErrorMessage(error), 'error')
      await loadCards(pagination.currentPage)
    } finally {
      setBusyCardId(null)
    }
  }

  const decodeRecord = (record) => {
    try {
      if (record.recordType === 'text') {
        const data = record.data

        if (typeof data === 'string') return data

        if (data) {
          const bytes = new Uint8Array(
            data.buffer,
            data.byteOffset,
            data.byteLength
          )

          // Payload NDEF Text : status byte + code langue + texte.
          if (bytes.length > 1) {
            const languageLength = bytes[0] & 0x3f
            const textBytes = bytes.slice(1 + languageLength)

            return new TextDecoder(
              record.encoding || 'utf-8'
            ).decode(textBytes)
          }

          return new TextDecoder().decode(bytes)
        }
      }

      if (record.recordType === 'url') {
        if (typeof record.data === 'string') return record.data

        const data = record.data

        if (data) {
          const bytes = new Uint8Array(
            data.buffer,
            data.byteOffset,
            data.byteLength
          )

          const prefixes = [
            '',
            'http://www.',
            'https://www.',
            'http://',
            'https://',
            'tel:',
            'mailto:',
          ]

          const prefix = prefixes[bytes[0]] ?? ''

          return prefix + new TextDecoder().decode(bytes.slice(1))
        }
      }

      if (record.data) {
        return new TextDecoder().decode(record.data)
      }

      return `Type : ${record.recordType}`
    } catch {
      return `Type : ${record.recordType}`
    }
  }

  /* ================= ÉCRITURE SUR LE TAG ================= */

  // Écrit une URL sur le tag physique (manuel ou automatique)
  const writeUrlToTag = async (url, auto = false) => {
    if (!url) {
      notify("Cette carte n'a pas d'URL publique à écrire.", 'error')
      return
    }

    if (!supported) {
      notify('Web NFC indisponible sur cet appareil.', 'error')
      return
    }

    try {
      setWriting(true)

      notify(
        auto
          ? 'Tag vierge détecté : gardez la carte contre le téléphone pour y écrire son URL.'
          : 'Approchez le tag pour y écrire l’URL de la carte.'
      )

      const ndef = new window.NDEFReader()

      await ndef.write({
        records: [{ recordType: 'url', data: url }],
      })

      addHistory('Écriture NFC', url)
      notify('URL écrite sur le tag.')
    } catch (error) {
      notify(getErrorMessage(error), 'error')
    } finally {
      setWriting(false)
    }
  }

  const writeCardUrlToTag = () => writeUrlToTag(scannedCard?.public_url)

  /* ================= CARTE DÉTECTÉE ================= */

  const selectScannedCard = (card, created) => {
    setScannedCard(card)
    setScanCreated(created)
    setCardForm({
      short_code: card.short_code || '',
      chip_uid: card.chip_uid || '',
      page_id: card.page_id ?? '',
    })
  }

  // Cherche la carte dans la liste déjà chargée (par UID, par code, puis par code dans l'URL du tag)
  const findLocalCard = (serial, foundRecords) => {
    const uid = normalizeUid(serial)
    const urls = foundRecords
      .filter((record) => record.type === 'url')
      .map((record) => record.value)

    return (
      cardsRef.current.find(
        (card) => uid && normalizeUid(card.chip_uid) === uid
      ) ||
      cardsRef.current.find(
        (card) => uid && normalizeUid(card.short_code) === uid
      ) ||
      cardsRef.current.find(
        (card) =>
          card.short_code &&
          urls.some((url) => url.includes(card.short_code))
      ) ||
      null
    )
  }

  // Carte inconnue → ajout automatique en base, visible tout de suite dans la liste.
  // POST /admin/cards/register : crée la carte si elle n'existe pas (par chip_uid),
  // sinon renvoie la carte existante. Réponse : { data: card, created: bool }
  const registerTag = async (serial, url) => {
    setRegistering(true)

    try {
      const response = await api.post('/admin/cards/register', {
        chip_uid: serial || null,
        url,
      })

      const card = response.data?.data || response.data
      const created = response.data?.created !== false

      selectScannedCard(card, created)

      if (created) {
        // Affichage immédiat dans le dashboard, avant même le rechargement
        setCards((previous) =>
          previous.some((item) => item.id === card.id)
            ? previous
            : [card, ...previous].slice(0, paginationRef.current.perPage)
        )

        setPagination((previous) => ({
          ...previous,
          total: previous.total + 1,
        }))
      }

      addHistory(
        created ? 'Carte ajoutée automatiquement' : 'Carte reconnue',
        card.short_code || serial || `#${card.id}`
      )

      notify(
        created
          ? 'Nouvelle carte ajoutée au dashboard.'
          : 'Carte déjà enregistrée.'
      )

      // Resynchronise avec la base (arrière-plan)
      loadCards(1, { silent: true })

      return card
    } catch (error) {
      notify(getErrorMessage(error), 'error')
      return null
    } finally {
      setRegistering(false)
    }
  }

  // Après lecture : carte connue → fiche ; carte inconnue → ajout automatique en base.
  // fromNfc = true quand le tag physique est réellement présent (écriture auto possible).
  const handleScannedTag = async (serial, foundRecords, { fromNfc = false } = {}) => {
    let card = findLocalCard(serial, foundRecords)

    if (card) {
      selectScannedCard(card, false)
      addHistory('Carte reconnue', card.short_code || `#${card.id}`)
    } else {
      const url =
        foundRecords.find((record) => record.type === 'url')?.value || null

      if (!serial && !url) {
        notify(
          "Aucun identifiant lisible sur ce tag : impossible de l'ajouter automatiquement.",
          'error'
        )
        return
      }

      card = await registerTag(serial, url)
    }

    // Tag vierge (aucune URL dessus) → on y écrit l'URL publique de la carte.
    // On n'écrase jamais un tag qui contient déjà une URL.
    if (
      fromNfc &&
      card &&
      autoWriteRef.current &&
      card.public_url &&
      !foundRecords.some((record) => record.type === 'url')
    ) {
      await writeUrlToTag(card.public_url, true)
    }
  }

  // Saisie manuelle : UID de la puce, code de la carte ou lien de la carte
  const submitManual = async () => {
    const value = manualValue.trim()

    if (!value) {
      notify('Saisissez un UID, un code de carte ou un lien.', 'error')
      return
    }

    const isUrl = /^https?:\/\/\S+$/i.test(value)

    setScannedCard(null)
    setRecords([])
    setSerialNumber(isUrl ? '' : value)

    await handleScannedTag(
      isUrl ? '' : value,
      isUrl ? [{ type: 'url', value }] : []
    )

    setManualValue('')
  }

  const saveScannedCard = async () => {
    if (!scannedCard) return

    setSavingScanned(true)

    try {
      // PUT /admin/cards/{card} : modifie les informations (page_id peut être null)
      const response = await api.put(`/admin/cards/${scannedCard.id}`, {
        short_code: cardForm.short_code.trim() || null,
        chip_uid: cardForm.chip_uid.trim() || null,
        page_id: cardForm.page_id === '' ? null : Number(cardForm.page_id),
      })

      const updated = response.data?.data || response.data
      const pageId = cardForm.page_id === '' ? null : Number(cardForm.page_id)

      const merged = {
        ...scannedCard,
        ...(updated?.id ? updated : {}),
        short_code: updated?.short_code ?? (cardForm.short_code.trim() || null),
        chip_uid: updated?.chip_uid ?? (cardForm.chip_uid.trim() || null),
        page_id: pageId,
        page:
          updated?.page ||
          pages.find((page) => Number(page.id) === pageId) ||
          null,
      }

      setScannedCard(merged)

      setCards((previous) =>
        previous.map((item) =>
          item.id === merged.id ? { ...item, ...merged } : item
        )
      )

      addHistory(
        'Modification de carte',
        merged.short_code || `#${merged.id}`
      )

      notify('Informations de la carte enregistrées.')
    } catch (error) {
      notify(getErrorMessage(error), 'error')
    } finally {
      setSavingScanned(false)
    }
  }

  const readNfc = async () => {
    setReadError('')
    setScannedCard(null)

    if (!supported) {
      setReadError(
        'Web NFC indisponible. Utilisez Chrome sur Android, avec HTTPS. Sur iPhone ou ordinateur, utilisez la saisie manuelle ci-dessous.'
      )
      return
    }

    try {
      abortRef.current?.abort()

      const controller = new AbortController()
      abortRef.current = controller

      setReading(true)
      setRecords([])
      setSerialNumber('')

      const ndef = new window.NDEFReader()

      ndef.onreading = (event) => {
        // Un seul scan par clic : on évite les lectures en double
        controller.abort()

        const serial = event.serialNumber || ''

        const foundRecords = Array.from(event.message.records).map(
          (record) => ({
            type: record.recordType,
            value: decodeRecord(record),
          })
        )

        setRecords(foundRecords)
        setSerialNumber(serial)
        setReading(false)

        addHistory(
          'Lecture NFC',
          `${foundRecords.length} enregistrement(s)${
            serial ? ` · UID ${serial}` : ''
          }`
        )

        notify('Lecture NFC terminée.')

        handleScannedTag(serial, foundRecords, { fromNfc: true })
      }

      ndef.onreadingerror = () => {
        setReadError('Impossible de lire les données de cette carte.')
        setReading(false)
      }

      await ndef.scan({ signal: controller.signal })
      notify('Approchez une carte NFC de votre appareil.')
    } catch (error) {
      setReading(false)
      setReadError(getErrorMessage(error))
    }
  }

  const writeNfc = async () => {
    const value = writeText.trim()

    if (!value) {
      notify('Saisissez un texte ou une URL.', 'error')
      return
    }

    if (!supported) {
      notify('Web NFC indisponible sur cet appareil.', 'error')
      return
    }

    try {
      setWriting(true)

      const ndef = new window.NDEFReader()

      // Si le texte est une URL, on écrit un vrai enregistrement URL.
      const isUrl = /^https?:\/\/\S+$/i.test(value)

      await ndef.write({
        records: [
          isUrl
            ? { recordType: 'url', data: value }
            : { recordType: 'text', data: value },
        ],
      })

      addHistory('Écriture NFC', value)
      notify('Écriture NFC terminée.')
      setWriteText('')
    } catch (error) {
      notify(getErrorMessage(error), 'error')
    } finally {
      setWriting(false)
    }
  }

  /* ---------- Briques d'affichage partagées ---------- */

  const tabStyle = (tab) => ({
    padding: isMobile ? '10px 12px' : '12px 16px',
    border: 'none',
    borderBottom:
      activeTab === tab
        ? '3px solid #9b7a43'
        : '3px solid transparent',
    background: 'transparent',
    color: activeTab === tab ? '#6f5129' : '#6b7280',
    fontWeight: activeTab === tab ? 700 : 500,
    fontSize: isMobile ? 13 : 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? 6 : 8,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  })

  // Bouton Activer / Désactiver d'une carte
  const renderToggleButton = (card, busy, fullWidth = false) => {
    const isActive = card.status === 'active'

    return (
      <button
        type="button"
        style={{
          ...secondary,
          ...(isActive ? { color: '#991b1b' } : {}),
          ...(fullWidth ? { flex: '1 1 100%' } : {}),
        }}
        disabled={busy}
        onClick={() =>
          changeCardStatus(card, isActive ? 'disable' : 'enable')
        }
      >
        {busy
          ? <Spinner size={15} />
          : isActive
            ? <PowerOff size={15} />
            : <Power size={15} />}
        {isActive ? 'Désactiver' : 'Activer'}
      </button>
    )
  }

  // Sélecteur de page associée à une carte (liste)
  const renderPageSelect = (card, busy, fullWidth = false) => (
    <select
      aria-label={`Associer une page à ${card.short_code}`}
      value={card.page_id ?? ''}
      disabled={busy || loadingPages || pages.length === 0}
      onChange={(event) => assignPage(card, event.target.value)}
      style={{
        ...input,
        width: fullWidth ? '100%' : 220,
        fontSize: isMobile ? 16 : 12,
      }}
    >
      <option value="">
        {pages.length === 0
          ? 'Aucune page disponible'
          : 'Choisir une page...'}
      </option>

      {pages.map((page) => (
        <option key={page.id} value={page.id}>
          {page.title || page.name || page.slug || `Page #${page.id}`}
        </option>
      ))}
    </select>
  )

  const cardPageLabel = (card) =>
    card.page?.title ||
    card.page?.name ||
    (card.page_id ? `Page #${card.page_id}` : 'Non associée')

  const activeCount = cards.filter(
    (card) => card.status === 'active'
  ).length

  const pendingCount = cards.filter(
    (card) => card.status === 'pending'
  ).length

  const scannedBusy = scannedCard && busyCardId === scannedCard.id

  const smallLabel = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#78716c',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  }

  return (
    <div
      style={{
        padding: isMobile ? 12 : compact ? 18 : 24,
        paddingBottom: isMobile ? 90 : undefined,
        maxWidth: 1400,
        margin: '0 auto',
        color: '#292524',
        boxSizing: 'border-box',
        width: '100%',
        overflowX: 'hidden',
      }}
    >
      <style>{`
        @keyframes nfc-spin { to { transform: rotate(360deg); } }
        .nfc-spin { animation: nfc-spin 1s linear infinite; }
        @keyframes nfc-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
        .nfc-live-dot { animation: nfc-pulse 2s ease-in-out infinite; }
      `}</style>

      {/* ===== EN-TÊTE ===== */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: isMobile ? 12 : 16,
          marginBottom: isMobile ? 14 : 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div
            style={{
              background: '#f4efe7',
              color: '#8b6b3f',
              borderRadius: 12,
              padding: isMobile ? 10 : 12,
              flexShrink: 0,
              display: 'flex',
            }}
          >
            <Radio size={isMobile ? 24 : 28} />
          </div>

          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: isMobile ? 22 : 27,
                fontWeight: 750,
              }}
            >
              NFC Tools
            </h1>
            <p
              style={{
                color: '#78716c',
                margin: '6px 0 0',
                fontSize: isMobile ? 13 : 15,
              }}
            >
              Gestion des cartes NFC et lecture/écriture des tags.
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            ...(isMobile ? { width: '100%' } : {}),
          }}
        >
          {supported && (
            <button
              type="button"
              style={{ ...button, ...(isMobile ? { flex: 1 } : {}) }}
              onClick={() => {
                setActiveTab('read')
                readNfc()
              }}
              disabled={reading || registering}
            >
              {reading ? <Spinner size={17} /> : <ScanLine size={17} />}
              Scanner
            </button>
          )}

          <button
            type="button"
            style={{ ...secondary, ...(isMobile ? { flex: 1 } : {}) }}
            onClick={() => {
              loadCards(pagination.currentPage)
              loadPages()
            }}
            disabled={loadingCards || loadingPages}
          >
            <RefreshCw size={17} />
            {isMobile ? 'Actualiser' : 'Actualiser les données'}
          </button>
        </div>
      </div>

      {/* ===== STATISTIQUES ===== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? 'repeat(3, minmax(0, 1fr))'
            : 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: isMobile ? 8 : 14,
        }}
      >
        {[
          ['Total des cartes', 'Total', pagination.total],
          ['Cartes actives (page)', 'Actives', activeCount],
          ['En attente (page)', 'En attente', pendingCount],
        ].map(([label, shortLabel, value]) => (
          <div
            key={label}
            style={{
              ...panel,
              marginTop: 0,
              padding: isMobile ? 10 : 22,
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            <div style={{ color: '#78716c', fontSize: isMobile ? 11 : 13 }}>
              {isMobile ? shortLabel : label}
            </div>
            <div
              style={{
                fontSize: isMobile ? 22 : 30,
                fontWeight: 750,
                marginTop: isMobile ? 4 : 8,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* ===== ONGLETS ===== */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderBottom: '1px solid #e7e5e4',
          marginTop: isMobile ? 16 : 22,
        }}
      >
        {[
          ['cards', CreditCard, 'Cartes Tapio'],
          ['read', ScanLine, 'Lecture NFC'],
          ['write', PenLine, 'Écriture NFC'],
          ['history', History, 'Historique'],
        ].map(([tab, Icon, label]) => (
          <button
            key={tab}
            type="button"
            style={tabStyle(tab)}
            onClick={() => setActiveTab(tab)}
          >
            <Icon size={isMobile ? 16 : 17} />
            {label}
          </button>
        ))}
      </div>

      {/* ===================== ONGLET CARTES ===================== */}
      {activeTab === 'cards' && (
        <div style={panel}>
          <h2 style={{ marginTop: 0, fontSize: isMobile ? 18 : 22 }}>
            Gestion des cartes Tapio
          </h2>

          <p style={{ color: '#78716c', fontSize: 13 }}>
            Les cartes sont enregistrées dans la base de données Laravel.
          </p>

          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'end',
              flexWrap: 'wrap',
              padding: isMobile ? 12 : 16,
              background: '#faf8f5',
              borderRadius: 10,
              marginBottom: 20,
            }}
          >
            <div style={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
              <label
                htmlFor="nfc-quantity"
                style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                }}
              >
                Nombre de cartes à créer
              </label>

              <input
                id="nfc-quantity"
                type="number"
                min="1"
                max="100"
                inputMode="numeric"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                style={{ ...input, width: isMobile ? '100%' : 180 }}
              />
            </div>

            <button
              type="button"
              style={{
                ...button,
                opacity: creatingCards ? 0.7 : 1,
                ...fullOnMobile,
              }}
              onClick={createCards}
              disabled={creatingCards}
            >
              {creatingCards
                ? <Spinner size={17} />
                : <Plus size={17} />}
              {creatingCards ? 'Création...' : 'Créer les cartes'}
            </button>
          </div>

          {pagesError && (
            <div
              role="alert"
              style={{
                padding: 12,
                borderRadius: 9,
                background: '#fff7ed',
                color: '#9a3412',
                marginBottom: 14,
                overflowWrap: 'anywhere',
              }}
            >
              <AlertCircle size={17} style={{ verticalAlign: 'middle' }} />
              {' '}{pagesError}
              <p style={{ marginBottom: 0, fontSize: 12 }}>
                Vérifiez que GET /pages est bien la route qui retourne les
                pages du compte connecté.
              </p>
            </div>
          )}

          {lastUpdated && (
            <p
              style={{
                fontSize: 12,
                color: '#78716c',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 6,
              }}
            >
              <span
                className="nfc-live-dot"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#16a34a',
                  display: 'inline-block',
                }}
              />
              En direct · mis à jour à {lastUpdated.toLocaleTimeString()}
              {' · '}Page {pagination.currentPage} / {pagination.lastPage}
            </p>
          )}

          {cardsError && (
            <div
              role="alert"
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: 15,
                borderRadius: 10,
                marginBottom: 16,
                overflowWrap: 'anywhere',
              }}
            >
              <strong>
                <AlertCircle
                  size={18}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Impossible de charger les cartes
              </strong>
              <p>{cardsError}</p>
              <button
                type="button"
                style={secondary}
                onClick={() => loadCards(pagination.currentPage)}
              >
                Réessayer
              </button>
            </div>
          )}

          {loadingCards && cards.length === 0 && (
            <p>
              <Spinner
                size={18}
                style={{ verticalAlign: 'middle', marginRight: 8 }}
              />
              Chargement...
            </p>
          )}

          {!loadingCards && !cardsError && cards.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: '#78716c' }}>
              <CreditCard size={34} />
              <p>Aucune carte sur cette page.</p>
            </div>
          )}

          {cards.length > 0 && (
            <>
              {/* ----- Grand écran : tableau ----- */}
              {!compact && (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                      minWidth: 1000,
                    }}
                  >
                    <thead>
                      <tr style={{ background: '#faf8f5' }}>
                        {[
                          'ID',
                          'Code carte',
                          'Chip UID',
                          'Page associée',
                          'Statut',
                          'Actions',
                        ].map((heading) => (
                          <th key={heading} style={styles.cell}>{heading}</th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {cards.map((card) => {
                        const busy = busyCardId === card.id

                        return (
                          <tr key={card.id}>
                            <td style={styles.cell}>#{card.id}</td>

                            <td style={styles.cell}>
                              <strong>{card.short_code || '—'}</strong>
                              {card.public_url && (
                                <div style={{ marginTop: 5 }}>
                                  <a
                                    href={card.public_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      color: '#8b6b3f',
                                      display: 'inline-flex',
                                      gap: 4,
                                      alignItems: 'center',
                                    }}
                                  >
                                    URL publique <ExternalLink size={12} />
                                  </a>
                                </div>
                              )}
                            </td>

                            <td
                              style={{
                                ...styles.cell,
                                fontFamily: 'monospace',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {card.chip_uid || '—'}
                            </td>

                            <td style={styles.cell}>
                              <div style={{ marginBottom: 7 }}>
                                {cardPageLabel(card)}
                              </div>

                              {renderPageSelect(card, busy)}
                            </td>

                            <td style={styles.cell}>
                              <StatusBadge status={card.status} />
                            </td>

                            <td style={styles.cell}>
                              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                                {renderToggleButton(card, busy)}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ----- Tablette / téléphone : liste de cartes ----- */}
              {compact && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? 'minmax(0, 1fr)'
                      : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 12,
                  }}
                >
                  {cards.map((card) => {
                    const busy = busyCardId === card.id

                    return (
                      <div
                        key={card.id}
                        style={{
                          border: '1px solid #eee7dd',
                          borderRadius: 12,
                          padding: 14,
                          background: '#fff',
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 10,
                            marginBottom: 12,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <strong style={{ fontSize: 16, overflowWrap: 'anywhere' }}>
                              {card.short_code || '—'}
                            </strong>
                            <div style={{ fontSize: 12, color: '#a8a29e', marginTop: 2 }}>
                              #{card.id}
                            </div>
                          </div>

                          <StatusBadge status={card.status} />
                        </div>

                        <div style={{ marginBottom: 12 }}>
                          <span style={smallLabel}>Chip UID</span>
                          <div
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 13,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {card.chip_uid || '—'}
                          </div>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                          <span style={smallLabel}>Page associée</span>
                          <div style={{ fontSize: 14, marginBottom: 8, overflowWrap: 'anywhere' }}>
                            {cardPageLabel(card)}
                          </div>

                          {renderPageSelect(card, busy, true)}
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          {renderToggleButton(card, busy, true)}

                          {card.public_url && (
                            <a
                              href={card.public_url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: '#8b6b3f',
                                display: 'inline-flex',
                                gap: 4,
                                alignItems: 'center',
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                            >
                              URL publique <ExternalLink size={13} />
                            </a>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* ----- Pagination ----- */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginTop: 18,
                }}
              >
                <span
                  style={{
                    color: '#78716c',
                    fontSize: 13,
                    ...(isMobile ? { width: '100%', textAlign: 'center' } : {}),
                  }}
                >
                  {pagination.total} carte(s) au total
                </span>

                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    ...(isMobile ? { width: '100%' } : {}),
                  }}
                >
                  <button
                    type="button"
                    style={{ ...secondary, ...(isMobile ? { flex: 1 } : {}) }}
                    disabled={
                      loadingCards || pagination.currentPage <= 1
                    }
                    onClick={() => loadCards(pagination.currentPage - 1)}
                  >
                    <ChevronLeft size={16} />
                    {isMobile ? 'Préc.' : 'Précédent'}
                  </button>

                  <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {pagination.currentPage} / {pagination.lastPage}
                  </span>

                  <button
                    type="button"
                    style={{ ...secondary, ...(isMobile ? { flex: 1 } : {}) }}
                    disabled={
                      loadingCards ||
                      pagination.currentPage >= pagination.lastPage
                    }
                    onClick={() => loadCards(pagination.currentPage + 1)}
                  >
                    {isMobile ? 'Suiv.' : 'Suivant'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===================== ONGLET LECTURE ===================== */}
      {activeTab === 'read' && (
        <div style={panel}>
          <h2 style={{ marginTop: 0, fontSize: isMobile ? 18 : 22 }}>
            Lire une carte NFC
          </h2>

          <div
            style={{
              padding: isMobile ? 12 : 16,
              borderRadius: 10,
              background: supported ? '#f0fdf4' : '#fff7ed',
              color: supported ? '#166534' : '#9a3412',
              marginBottom: 18,
              display: 'flex',
              gap: 10,
            }}
          >
            <span style={{ flexShrink: 0, display: 'flex' }}>
              {supported ? <CheckCircle2 size={21} /> : <Smartphone size={21} />}
            </span>
            <div style={{ minWidth: 0 }}>
              <strong>
                {supported ? 'Web NFC disponible' : 'Web NFC non détecté'}
              </strong>
              <p style={{ marginBottom: 0, fontSize: 13 }}>
                {supported
                  ? 'Lancez le scan puis approchez une carte compatible. Si elle n’existe pas encore, elle sera ajoutée automatiquement au dashboard.'
                  : 'La lecture NFC fonctionne avec Chrome sur Android, en HTTPS. Sur iPhone ou ordinateur, utilisez la saisie manuelle plus bas.'}
              </p>
            </div>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              marginBottom: 16,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={autoWrite}
              onChange={(event) => setAutoWrite(event.target.checked)}
              style={{
                width: isMobile ? 22 : 16,
                height: isMobile ? 22 : 16,
                marginTop: 1,
                flexShrink: 0,
              }}
            />
            <span>
              <strong>Écrire automatiquement l’URL sur les tags vierges.</strong>
              {' '}Un tag qui contient déjà une URL n’est jamais modifié.
            </span>
          </label>

          <button
            type="button"
            style={{ ...button, ...(isMobile ? { width: '100%' } : {}) }}
            onClick={readNfc}
            disabled={reading || registering || !supported}
          >
            {reading
              ? <Spinner size={18} />
              : <ScanLine size={18} />}
            {reading ? 'Approchez la carte...' : 'Scanner une carte'}
          </button>

          {readError && (
            <p role="alert" style={{ color: '#b91c1c', marginTop: 16 }}>
              {readError}
            </p>
          )}

          {serialNumber && (
            <p style={{ marginTop: 16, fontSize: 13, overflowWrap: 'anywhere' }}>
              Numéro de série (UID) :{' '}
              <strong style={{ fontFamily: 'monospace' }}>
                {serialNumber}
              </strong>
            </p>
          )}

          {records.map((record, index) => (
            <div
              key={`${record.type}-${index}`}
              style={{
                marginTop: 12,
                padding: 14,
                border: '1px solid #eee7dd',
                borderRadius: 9,
              }}
            >
              <strong>{record.type}</strong>
              <p style={{ marginBottom: 0, overflowWrap: 'anywhere' }}>
                {record.value}
              </p>
            </div>
          ))}

          {registering && (
            <p style={{ marginTop: 18, color: '#5f4930' }}>
              <Spinner
                size={18}
                style={{ verticalAlign: 'middle', marginRight: 8 }}
              />
              Recherche / ajout de la carte dans la base de données...
            </p>
          )}

          {/* ===== FICHE DE LA CARTE DÉTECTÉE ===== */}
          {scannedCard && (
            <div
              style={{
                marginTop: 22,
                padding: isMobile ? 14 : 18,
                border: '1px solid #e7ded1',
                borderRadius: 12,
                background: '#faf8f5',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 16 }}>
                    Carte #{scannedCard.id}
                  </strong>
                  <div style={{ fontSize: 12, color: '#78716c', marginTop: 4 }}>
                    {scanCreated
                      ? 'Ajoutée automatiquement au dashboard'
                      : 'Carte déjà enregistrée'}
                  </div>
                </div>

                <StatusBadge status={scannedCard.status} />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
                  gap: 14,
                }}
              >
                <div>
                  <label
                    htmlFor="scan-short-code"
                    style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}
                  >
                    Code carte
                  </label>
                  <input
                    id="scan-short-code"
                    type="text"
                    value={cardForm.short_code}
                    onChange={(event) =>
                      setCardForm((previous) => ({
                        ...previous,
                        short_code: event.target.value,
                      }))
                    }
                    style={{ ...input, width: '100%' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="scan-chip-uid"
                    style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}
                  >
                    Chip UID
                  </label>
                  <input
                    id="scan-chip-uid"
                    type="text"
                    value={cardForm.chip_uid}
                    onChange={(event) =>
                      setCardForm((previous) => ({
                        ...previous,
                        chip_uid: event.target.value,
                      }))
                    }
                    style={{
                      ...input,
                      width: '100%',
                      fontFamily: 'monospace',
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="scan-page"
                    style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}
                  >
                    Page associée
                  </label>
                  <select
                    id="scan-page"
                    value={cardForm.page_id}
                    disabled={loadingPages}
                    onChange={(event) =>
                      setCardForm((previous) => ({
                        ...previous,
                        page_id: event.target.value,
                      }))
                    }
                    style={{ ...input, width: '100%' }}
                  >
                    <option value="">Aucune page</option>

                    {pages.map((page) => (
                      <option key={page.id} value={page.id}>
                        {page.title || page.name || page.slug ||
                          `Page #${page.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginTop: 18,
                }}
              >
                <button
                  type="button"
                  style={{
                    ...button,
                    opacity: savingScanned ? 0.7 : 1,
                    ...fullOnMobile,
                  }}
                  onClick={saveScannedCard}
                  disabled={savingScanned}
                >
                  {savingScanned
                    ? <Spinner size={17} />
                    : <Save size={17} />}
                  Enregistrer les modifications
                </button>

                {renderToggleButton(scannedCard, scannedBusy, isMobile)}

                {scannedCard.public_url && (
                  <button
                    type="button"
                    style={{ ...secondary, ...fullOnMobile }}
                    disabled={writing || !supported}
                    onClick={writeCardUrlToTag}
                  >
                    {writing
                      ? <Spinner size={15} />
                      : <PenLine size={15} />}
                    Écrire l’URL sur le tag
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ===== SAISIE MANUELLE ===== */}
          <div
            style={{
              marginTop: 24,
              padding: isMobile ? 12 : 16,
              border: '1px dashed #d6cbbb',
              borderRadius: 10,
            }}
          >
            <strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Keyboard size={17} /> Sans NFC : ajouter ou retrouver une carte
            </strong>
            <p style={{ color: '#78716c', fontSize: 13 }}>
              Saisissez l’UID de la puce, le code de la carte ou son lien.
              La carte est retrouvée ou ajoutée au dashboard comme avec un scan.
            </p>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                value={manualValue}
                onChange={(event) => setManualValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submitManual()
                }}
                placeholder="04:A1:B2:C3:D4:E5:F6 ou https://…"
                aria-label="UID, code ou lien de la carte"
                style={{
                  ...input,
                  flex: '1 1 220px',
                  minWidth: 0,
                  fontFamily: 'monospace',
                }}
              />

              <button
                type="button"
                style={{ ...button, ...fullOnMobile }}
                onClick={submitManual}
                disabled={registering}
              >
                {registering ? <Spinner size={17} /> : <Plus size={17} />}
                Ajouter / retrouver
              </button>
            </div>
          </div>

          <p style={{ color: '#78716c', fontSize: 13, marginTop: 20 }}>
            L’UID n’est disponible que si le navigateur l’expose pour ce
            type de puce. Sans UID, la carte est identifiée par l’URL
            contenue dans le tag.
          </p>
        </div>
      )}

      {/* ===================== ONGLET ÉCRITURE ===================== */}
      {activeTab === 'write' && (
        <div style={panel}>
          <h2 style={{ marginTop: 0, fontSize: isMobile ? 18 : 22 }}>
            Écrire sur une carte NFC
          </h2>

          <p style={{ color: '#78716c' }}>
            Une URL valide sera écrite comme enregistrement URL ; les autres
            contenus seront écrits comme texte.
          </p>

          <label
            htmlFor="nfc-write-text"
            style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}
          >
            Texte ou URL
          </label>

          <textarea
            id="nfc-write-text"
            value={writeText}
            onChange={(event) => setWriteText(event.target.value)}
            placeholder="https://votre-site.com ou votre texte..."
            rows={4}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid #d6d3d1',
              borderRadius: 9,
              padding: 12,
              font: 'inherit',
              fontSize: isMobile ? 16 : undefined,
              resize: 'vertical',
              marginBottom: 16,
            }}
          />

          <button
            type="button"
            style={{ ...button, ...(isMobile ? { width: '100%' } : {}) }}
            onClick={writeNfc}
            disabled={writing || !supported}
          >
            {writing
              ? <Spinner size={18} />
              : <PenLine size={18} />}
            {writing ? 'Écriture en cours...' : 'Écrire sur la carte'}
          </button>

          {!supported && (
            <p style={{ color: '#9a3412', fontSize: 13 }}>
              Un appareil et un navigateur compatibles (Chrome sur Android,
              HTTPS) sont nécessaires pour écrire sur un tag NFC physique.
            </p>
          )}
        </div>
      )}

      {/* ===================== ONGLET HISTORIQUE ===================== */}
      {activeTab === 'history' && (
        <div style={panel}>
          <h2 style={{ marginTop: 0, fontSize: isMobile ? 18 : 22 }}>
            Historique de la session
          </h2>

          {history.length === 0 ? (
            <p style={{ color: '#78716c' }}>
              Aucune opération enregistrée pendant cette session.
            </p>
          ) : (
            history.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: isMobile ? '12px 0' : 14,
                  borderBottom: '1px solid #eee7dd',
                  overflowWrap: 'anywhere',
                }}
              >
                <strong>{item.action}</strong>
                <div style={{ color: '#57534e', marginTop: 5 }}>
                  {item.details}
                </div>
                <div style={{ color: '#a8a29e', fontSize: 12, marginTop: 5 }}>
                  {item.date}
                </div>
              </div>
            ))
          )}

          <p style={{ color: '#78716c', fontSize: 12, marginTop: 16 }}>
            Cet historique reste local à la session. Sa sauvegarde permanente
            nécessite une API et une table dédiées.
          </p>
        </div>
      )}

      {/* ===== NOTIFICATION ===== */}
      {notice && (
        <div
          role="status"
          style={{
            position: 'fixed',
            right: isMobile ? 12 : 24,
            left: isMobile ? 12 : 'auto',
            bottom: isMobile
              ? 'calc(12px + env(safe-area-inset-bottom))'
              : 24,
            zIndex: 1000,
            maxWidth: isMobile ? 'none' : 380,
            boxSizing: 'border-box',
            background: notice.type === 'error' ? '#991b1b' : '#166534',
            color: '#fff',
            padding: '14px 18px',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            display: 'flex',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <span style={{ flexShrink: 0, display: 'flex' }}>
            {notice.type === 'error'
              ? <AlertCircle size={20} />
              : <CheckCircle2 size={20} />}
          </span>

          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {notice.message}
          </span>

          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Fermer"
            style={{
              marginLeft: 8,
              border: 'none',
              background: 'transparent',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 22,
              lineHeight: 1,
              padding: isMobile ? 8 : 0,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  )
}

export default AdminNfcTools